/**
 * The undo stack, as something that can be driven by a test.
 *
 * It used to live inside `task-workspace-page`, a 2,200-line component that also owns routing,
 * saving, drag and drop, Flow, Preview and the phone session. Nothing could exercise a SEQUENCE of
 * undos without standing all of that up, so every test was one field on a freshly mounted block —
 * and six consecutive fixes, each green, made the feature worse without a single failure to show
 * for it. The rules those fixes kept guessing at are written down in `docs/undo-redo-contract.md`;
 * this is the seam that lets them be tested.
 *
 * What stays here: the stack itself, the cursor, the debounce that decides where one step ends and
 * the next begins, and the button state derived from all three.
 *
 * What stays with the caller: applying the state an op produces, and everything the VIEW does about
 * it — opening the block, revealing the surface, ringing the field. Those need the page's own
 * selection and panels, and the harness supplies its own, which is the whole point of the split.
 */
import { useEffect, useRef, useState } from 'react';

import { changedFields } from './changed-fields';
import { continuesSession } from './edit-coalescing';
import { actedOn, watchFieldProvenance } from './field-provenance';
import { applyOp, invertOp, type Op, type StackState } from './undo-ops';
import { recordUndoEvent } from './undo-recorder';

type Data = Record<string, unknown>;

/** Declared with the ops it is applied to; re-exported here, where the callers look. */
export type { StackState };

/** A history, out of the hook and back in again — see `peek` and `restore`. */
export interface StackHistory {
  readonly ops: readonly Op[];
  readonly cursor: number;
}

export interface UndoStackOptions {
  /** Published tasks are read-only; undo and redo do nothing rather than half-working. */
  readOnly: boolean;
  getState: () => StackState;
  /** Put the state an op produced into the world. Called once per undo/redo. */
  applyState: (next: StackState) => void;
  /**
   * What the view should do about a step that has just been applied — open the block, reveal the
   * surface, ring the field, flash a restored block. `before` is the state as it was, which is the
   * only way to find a block that a redo is about to move.
   */
  onApplied: (op: Op, direction: 'undo' | 'redo', before: StackState) => void;
  /** How a step describes itself on the button. */
  describeBlockEdit: (blockType: string) => string;
}

export interface UndoStack {
  /**
   * A block's data changed. Call BEFORE applying it: the baseline is read from current state.
   *
   * Returns nothing — recording a step and applying the edit are deliberately separate, because the
   * caller applies the edit whether or not it turns out to be worth a step.
   */
  recordBlockUpdate: (blockId: string, next: Data) => void;
  /** Everything that is not a block-data edit: adds, removes, moves, pastes, variables, the title. */
  push: (op: Op) => void;
  /** Settle any in-flight edit onto the stack now. */
  flush: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
  /** Throw the history away — a different task is being loaded into the same page. */
  reset: () => void;
  /**
   * Put a history BACK, for a task being reopened in the same session.
   *
   * The counterpart to `peek`: park what it returns while the teacher is elsewhere, hand it back
   * here when they return, and stepping away stops being the same thing as closing the editor.
   * The caller owns the decision of whether a parked history is still valid — this hook cannot
   * know, because what invalidates one is the CONTENT having moved underneath it.
   *
   * `null` empties the stack, which is what `reset` is.
   */
  restore: (history: StackHistory | null) => void;
  /**
   * The steps, oldest first, and where the cursor sits.
   *
   * Read-only, and here because "what did that edit actually record?" is a question the contract
   * asks (A5, B3) and nothing could answer. An op wider than the act that caused it is a defect
   * whether or not anyone notices the extra ring.
   */
  peek: () => { ops: readonly Op[]; cursor: number };
}

/** How long an edit waits before it becomes its own step. Typing, mostly. */
const SETTLE_MS = 800;

export function useUndoStack(opts: UndoStackOptions): UndoStack {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const opsRef = useRef<Op[]>([]);
  const cursorRef = useRef(-1);
  const pendingRef = useRef<{
    blockId: string;
    prev: Data;
    next: Data;
    /** Every field this session has touched — see `continuesSession` for what it decides. */
    fields: Set<string>;
    /**
     * The control the teacher was on when this session STARTED, not when it settles.
     *
     * Read at flush time it is simply wrong: a session settles when the teacher moves on, so the
     * act recorded is whatever they touched on the way out. Typing a feedback message and then
     * collapsing the card put the ring on the card — which, collapsed, shows a title and nothing
     * else — instead of on the message that changed.
     */
    act: string | null;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [undoLabel, setUndoLabel] = useState<string | null>(null);
  const [redoLabel, setRedoLabel] = useState<string | null>(null);
  /**
   * An edit that has not reached the stack yet still counts as something to undo.
   *
   * Every path out of a pending edit has to clear this, or the flag outlives what it describes:
   * Undo stays lit after the whole stack is drained, and pressing it flushes nothing and undoes
   * nothing — a step with no visible effect at the end of the stack. Two of the three paths used
   * to miss it.
   */
  const [hasPending, setHasPending] = useState(false);

  // Watches which control the teacher touches, so a step can tell its act from its fallout.
  useEffect(watchFieldProvenance, []);

  function syncFlags() {
    const ops = opsRef.current;
    const c = cursorRef.current;
    setCanUndo(c >= 0);
    setCanRedo(c < ops.length - 1);
    setUndoLabel(c >= 0 ? (ops[c]?.label ?? null) : null);
    setRedoLabel(c < ops.length - 1 ? (ops[c + 1]?.label ?? null) : null);
  }

  function pushDirect(op: Op) {
    const dropped = opsRef.current.length - (cursorRef.current + 1);
    opsRef.current = opsRef.current.slice(0, cursorRef.current + 1);
    opsRef.current.push(op);
    cursorRef.current = opsRef.current.length - 1;
    // `dropped` is the redo tail this push threw away. A push that lands mid-run is how a redo
    // stack disappears under a teacher who was stepping forward through it.
    recordUndoEvent('record', {
      op: op.type, label: op.label, dropped,
      blockId: op.type === 'update-block' ? op.blockId : undefined,
      fields: op.type === 'update-block' ? op.fields : undefined,
      depth: opsRef.current.length, cursor: cursorRef.current,
    });
    syncFlags();
  }

  /** The touched field first, then the rest in diff order. */
function actFirst(fields: string[], act: string | null): string[] {
  return act && fields.includes(act) ? [act, ...fields.filter(f => f !== act)] : fields;
}

/**
 * A container never leads its own contents — contract D2, as a property of the list rather than a
 * hope about ordering.
 *
 * The reveal takes the first field it can reach, so `default-unanswered` ahead of
 * `default-unanswered.content` rings a COLLAPSED card, which shows a title and nothing else.
 * Provenance alone cannot prevent it: a rich-text editor reports its change on a debounce, so the
 * teacher has often clicked the card shut before the edit is ever recorded, and the act honestly
 * IS the card by then. The ordering rule holds whatever the timing did.
 */
function finestFirst(fields: string[]): string[] {
  const contains = (f: string) => fields.some(other => other !== f && other.startsWith(`${f}.`));
  return [...fields.filter(f => !contains(f)), ...fields.filter(contains)];
}

/** The step a settled edit becomes, or null when it turned out to be nothing. */
  function stepFor(p: { blockId: string; prev: Data; next: Data; act: string | null }): Op | null {
    if (JSON.stringify(p.prev) === JSON.stringify(p.next)) return null;
    const type = optsRef.current.getState().blocks.find(b => b.id === p.blockId)?.type ?? '';
    return {
      type: 'update-block',
      label: optsRef.current.describeBlockEdit(type),
      blockId: p.blockId,
      prev: p.prev,
      next: p.next,
      // Carried on the op rather than re-diffed per undo — and carried on BOTH paths onto the
      // stack. The flush path used to omit them, so an edit settled by pressing Undo lost the
      // very precision the field ids exist to give and fell back to flashing the whole block.
      //
      // The field the teacher actually touched goes FIRST, because the reveal takes the first it
      // can find and a diff has no idea which of its entries is the act. One click on "accept
      // integer answers only" changes six fields; five of them are consequences.
      fields: finestFirst(actFirst(changedFields(p.prev, p.next), p.act)),
    };
  }

  function flush() {
    setHasPending(false);
    const p = pendingRef.current;
    if (!p) return;
    clearTimeout(p.timer);
    pendingRef.current = null;
    const op = stepFor(p);
    if (op) pushDirect(op);
  }

  /**
   * How long a run of same-key flow edits keeps merging into one step.
   *
   * Deliberately the same window `recordBlockUpdate` settles on, because it is answering the same
   * question — has the teacher stopped touching this thing — and two different idles would make
   * "one step per edit" mean two different things depending on which surface you were on.
   */
  const COALESCE_MS = 600;
  const lastPushRef = useRef<{ key: string; at: number } | null>(null);

  function push(op: Op) {
    flush();
    /**
     * A run of edits to the SAME thing becomes one step.
     *
     * `update-flow` and `update-variable` carry a key, and only for edits that fire per keystroke
     * — a flow rule's title, a variable's equation or range. The key includes the op type via the
     * `head?.type === op.type` check below, so two different kinds of edit can never merge. The merge
     * rewrites the head op's `next` and keeps its `prev`, so undoing once returns to before the run
     * started — the whole point. Anything without a key pushes normally and resets the run, so an
     * unrelated edit between two title keystrokes correctly splits them into two steps.
     */
    const key = op.type === 'update-flow' || op.type === 'update-variable' ? op.coalesce : undefined;
    const last = lastPushRef.current;
    const head = opsRef.current[cursorRef.current];
    if (
      key && last && last.key === key && Date.now() - last.at < COALESCE_MS
      && head?.type === op.type && cursorRef.current === opsRef.current.length - 1
    ) {
      opsRef.current[cursorRef.current] = { ...head, next: (op as Extract<Op, { next: unknown }>).next } as Op;
      lastPushRef.current = { key, at: Date.now() };
      syncFlags();
      return;
    }
    lastPushRef.current = key ? { key, at: Date.now() } : null;
    pushDirect(op);
  }

  function recordBlockUpdate(blockId: string, next: Data) {
    let p = pendingRef.current;
    if (p && p.blockId !== blockId) { flush(); p = null; }

    // One step per thing changed, not per settle window — see `continuesSession`.
    if (p && !continuesSession(p.fields, changedFields(p.next, next))) { flush(); p = null; }

    const prev = p ? p.prev : (optsRef.current.getState().blocks.find(b => b.id === blockId)?.data ?? {});
    if (p) clearTimeout(p.timer);

    const timer = setTimeout(() => {
      const pending = pendingRef.current;
      if (!pending || pending.blockId !== blockId) return;
      pendingRef.current = null;
      // Landed, or turned out to be nothing: either way it is no longer PENDING.
      setHasPending(false);
      const op = stepFor(pending);
      if (op) pushDirect(op);
    }, SETTLE_MS);

    const delta = changedFields(prev, next);
    pendingRef.current = {
      blockId, prev, next, timer,
      fields: new Set([...(p?.fields ?? []), ...delta]),
      // Captured now, while the teacher is still on the control — see the field's doc comment.
      act: p?.act ?? actedOn(delta),
    };
    if (JSON.stringify(prev) !== JSON.stringify(next)) setHasPending(true);
  }

  function step(direction: 'undo' | 'redo') {
    if (optsRef.current.readOnly) return;
    flush();
    const ops = opsRef.current;

    if (direction === 'undo') {
      if (cursorRef.current < 0) return;
    } else if (cursorRef.current >= ops.length - 1) return;

    if (direction === 'redo') cursorRef.current++;
    const op = ops[cursorRef.current]!;
    const before = optsRef.current.getState();

    // The block this op names, as it stands BEFORE and AFTER. An op whose block is missing applies
    // to nothing and the stack advances in silence — the shape of "it highlighted but did not
    // change anything".
    const target = op.type === 'update-block' ? op.blockId : undefined;
    const dataBefore = target
      ? JSON.stringify(before.blocks.find(b => b.id === target)?.data)
      : undefined;

    optsRef.current.applyState(applyOp(direction === 'undo' ? invertOp(op) : op, before));
    optsRef.current.onApplied(op, direction, before);

    if (direction === 'undo') cursorRef.current--;
    const after = optsRef.current.getState();
    recordUndoEvent('step', {
      direction, op: op.type, label: op.label, blockId: target,
      fields: op.type === 'update-block' ? op.fields : undefined,
      blockPresent: target ? after.blocks.some(b => b.id === target) : undefined,
      // false here means the op named a block whose data did not move: the step did nothing.
      dataMoved: target ? JSON.stringify(after.blocks.find(b => b.id === target)?.data) !== dataBefore : undefined,
      depth: opsRef.current.length, cursor: cursorRef.current,
    });
    syncFlags();
  }

  /**
   * Replace the history wholesale, with a parked one or with nothing.
   *
   * A pending edit is DROPPED rather than flushed: it belongs to the content being replaced, and
   * settling it onto the incoming history would file an edit to one task as a step in another's.
   * A caller that wants it kept flushes before calling (the page does, on its way out).
   *
   * The ops are copied in, so a history parked here and later pushed onto cannot mutate the
   * caller's copy of it.
   */
  function restore(history: StackHistory | null) {
    const p = pendingRef.current;
    if (p) clearTimeout(p.timer);
    pendingRef.current = null;
    opsRef.current = history ? [...history.ops] : [];
    cursorRef.current = history ? history.cursor : -1;
    setHasPending(false);
    syncFlags();
  }

  function reset() {
    restore(null);
  }

  return {
    reset,
    restore,
    peek: () => ({ ops: opsRef.current, cursor: cursorRef.current }),
    recordBlockUpdate,
    push,
    flush,
    undo: () => step('undo'),
    redo: () => step('redo'),
    canUndo: canUndo || hasPending,
    canRedo,
    undoLabel,
    redoLabel,
  };
}
